import React, {
  ChangeEvent,
  CSSProperties,
  ReactElement,
  useState,
  ReactNode,
  KeyboardEventHandler,
} from "react";
import styled from "styled-components";

import Text from "components/Text";
import {
  BrandColorPalette,
  PrimaryColorPalette,
  StatusColorPalette,
  TextAndShapesPalette,
} from "lib/styles/colors";
import { TextType } from "lib/design-system/typography";
import responsiveStyling, { responsiveStylingMax } from "lib/responsive";
import { BaseStyledInput } from "components/beta/BaseStyledInput";

export enum InputTypes {
  DEFAULT = "default",
  PASSWORD = "password",
}

interface StyledInputProps {
  disabled?: boolean;
  width?: string;
  height?: string;
  border?: string;
  backgroundColor?: string;
}

const InputContainer = styled.div<StyledInputProps>`
  display: flex;
  flex-direction: column;
  height: 60px;
  width: 280px;
  align-items: center;
  padding: 2px 16px;
  justify-content: flex-start;
  ${responsiveStyling({
    ">768": `
      align-items: center;
      justify-content: space-between;
      flex-direction: row;
    `,
  })};
  ${({ width }) =>
    width &&
    `
    @media screen and (min-width: 768px) {
      width: ${width ? width : "368px"};
    }
  `}
  ${responsiveStylingMax({
    "<480": `
      width: 90%;
        justify-content: center;
    `,
    "<768": `
      width: 90%;
      justify-content: center;
    `,
  })}
  ${({ height }) =>
    height &&
    `
      height: ${height ? height : "60px"};
  `}
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border-radius: 8px;
  background-color: ${({ backgroundColor }) => backgroundColor ?? "white"};
  border: 2px solid ${PrimaryColorPalette.MAIN_3};
  ${({ border }) =>
    `border: ${border ?? `1px solid ${TextAndShapesPalette.INPUT_FIELD}`};`}
  /* border: 1px solid ${TextAndShapesPalette.INPUT_FIELD}; */
  color: ${PrimaryColorPalette.MAIN_0};
  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
  `}
`;

const TextContainer = styled.div`
  width: 100%;
  ${responsiveStyling({
    ">480": `
      width: auto;
    `,
  })};
`;

interface Props {
  label?: string;
  type?: string;
  placeholder?: string;
  iconLeft?: ReactNode;
  value?: string;
  error: boolean;
  disabled?: boolean;
  width?: string;
  height?: string;
  inputBorderOverride?: string; // full border setting
  onBlur?: () => void;
  onFocus?: () => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyUp?: KeyboardEventHandler<HTMLDivElement>;
  backgroundColor?: string;
}

export function Input(props: Props): ReactElement {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const { containerStyle } = aggregateStyles({ isFocused, isHovering, props });

  /**
   * state style handlers
   */
  const handleFocus = () => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus();
    }
  };
  const handleBlur = () => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur();
    }
  };
  const handleMouseOver = () => setIsHovering(true);
  const handleMouseOut = () => setIsHovering(false);

  return (
    <InputContainer
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onMouseDown={handleFocus}
      onKeyUp={props.onKeyUp}
      style={containerStyle}
      disabled={props.disabled ?? false}
      width={props.width}
      height={props.height}
      border={props.inputBorderOverride}
      backgroundColor={props.backgroundColor}
    >
      {props.label ||
        (props.iconLeft && (
          <TextContainer>
            {props.iconLeft ? (
              props.iconLeft
            ) : (
              <Text
                style={{ paddingTop: 4, width: "100%" }}
                fontColor={
                  !props.error
                    ? PrimaryColorPalette.MAIN_0
                    : StatusColorPalette.DANGER_STATUS
                }
                type={TextType.PARAGRAPH}
              >
                {props.label ?? ""}
              </Text>
            )}
          </TextContainer>
        ))}
      <BaseStyledInput
        onChange={props.onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={props.value ?? ""}
        placeholder={props.placeholder}
        type={props.type ?? "text"}
        disabled={props.disabled ?? false}
        width={props.width}
      />
    </InputContainer>
  );
}

//ERROR STATES SHOULD BE MOVED ON FIT AND FINISH
const ErrorState: CSSProperties = {
  background: StatusColorPalette.DANGER_BACKGROUND,
  borderColor: StatusColorPalette.DANGER_STATUS,
  color: StatusColorPalette.DANGER_STATUS,
};

const Active: CSSProperties = {
  // background: BackgroundColorPalette.BASIC_DECORATION_BLUE,
  borderColor: BrandColorPalette.BRANDING,
};

const Hover: CSSProperties = {
  // background: BackgroundColorPalette.BASIC_DECORATION_BLUE,
  borderColor: BrandColorPalette.BRANDING,
};

const Default: CSSProperties = {};

function aggregateStyles(options: {
  props: Props;
  isFocused: boolean;
  isHovering: boolean;
}): {
  containerStyle: CSSProperties;
} {
  const { isFocused, isHovering, props } = options;
  let containerStyle: CSSProperties;

  if (props.error) {
    containerStyle = ErrorState;
  } else if (isFocused && !props.disabled) {
    containerStyle = Active;
  } else if (isHovering && !props.disabled) {
    containerStyle = Hover;
  } else {
    containerStyle = Default;
  }

  return {
    containerStyle,
  };
}
